import type { IAssembly, ISummaryData } from 'containers/KitWorkspaceStorage/types';

export interface ISpecLine {
  label: string;
  value: string;
}

const lengthLine = (s: ISummaryData): string | undefined => {
  if (s.lengthType === 'feet' && typeof s.lengthInFeet === 'number') {
    return `${s.lengthInFeet} feet`;
  }
  if (typeof s.lengthInInches === 'number') {
    return `${s.lengthInInches} inches`;
  }
  return undefined;
};

/**
 * Flatten an assembly's summaryData into a label/value spec list for cards.
 * Tolerates both the hose domain (hose/fittings/length) and a generic `spec`
 * list used by non-hose tenants (e.g. hydrapark).
 */
export const buildSpecLines = (assembly: IAssembly): ISpecLine[] => {
  const s = assembly.summaryData || {};
  if (s.spec && s.spec.length) {
    return s.spec.filter((l) => l && l.value).map((l) => ({ label: l.label, value: l.value }));
  }
  const lines: ISpecLine[] = [];
  if (s.hose?.title) lines.push({ label: 'Hose', value: s.hose.title });
  if (s.fitting1?.title) lines.push({ label: 'Fitting 1', value: s.fitting1.title });
  if (s.fitting2?.title) lines.push({ label: 'Fitting 2', value: s.fitting2.title });
  const len = lengthLine(s);
  if (len) lines.push({ label: 'Length', value: len });
  if (typeof s.angleOfRotation === 'number') lines.push({ label: 'Rotation', value: `${s.angleOfRotation}°` });
  if (s.accessories?.title) lines.push({ label: 'Accessory', value: s.accessories.title });
  if (s.customerPartNumber) lines.push({ label: 'Customer Part Number', value: s.customerPartNumber });
  return lines;
};

/**
 * Configured Assembly Qty (set in the wizard, stored on summaryData). Defaults
 * to 1 when absent or invalid. Used to seed the kit-line qty and to badge
 * standalone assembly cards.
 */
export const assemblyQty = (assembly: IAssembly): number => {
  const q = assembly.summaryData?.quantity;
  return typeof q === 'number' && q > 0 ? q : 1;
};

/**
 * Display name for an assembly: only the user-entered name. We intentionally do
 * NOT fall back to the part number / summaryData.title here — an assembly with
 * no name is shown as "Untitled assembly" (see AssemblyName) so the user is
 * nudged to name it.
 */
export const assemblyDisplayName = (assembly: IAssembly): string | undefined => {
  const name = assembly.name?.trim();
  return name || undefined;
};

/**
 * Compact one-line spec for cards / dialog subtitles, e.g.
 * "FC332-06 · 2× 1G4FR6TZ · 30 in · acc 624-11". Identical fittings collapse to
 * "2× <code>". Falls back to the generic spec list for non-hose tenants.
 */
export const buildSpecSummary = (assembly: IAssembly): string => {
  const s = assembly.summaryData || {};
  if (s.spec && s.spec.length) {
    return s.spec.filter((l) => l && l.value).map((l) => l.value).join(' · ');
  }
  const parts: string[] = [];
  if (s.hose?.title) parts.push(s.hose.title);

  const f1 = s.fitting1?.title;
  const f2 = s.fitting2?.title;
  if (f1 && f2 && f1 === f2) {
    parts.push(`2× ${f1}`);
  } else {
    if (f1) parts.push(f1);
    if (f2) parts.push(f2);
  }

  if (s.lengthType === 'feet' && s.lengthInFeet) {
    parts.push(`${s.lengthInFeet} ft`);
  } else if (s.lengthInInches) {
    parts.push(`${s.lengthInInches} in`);
  }

  if (typeof s.angleOfRotation === 'number' && s.angleOfRotation) {
    parts.push(`${s.angleOfRotation}°`);
  }
  if (s.accessories?.title) parts.push(`acc ${s.accessories.title}`);
  return parts.join(' · ');
};
